import { AdultType, CmsProperty, ExtraDataType, FocusType } from '../../api/models/Shared.types';
import { Environment } from '../../config/environment';
import { ottActionFilter } from '../../api/models/ActionFilter';
import { formatDate } from '../utility/DateFormatter';
import { Log } from '@lightningjs/sdk';
import { CmsApi } from '../../api/CmsApi';
import { FavoriteRequestType, FavoriteRibbonRequest } from './FavoriteRibbonRequest';
import { Backend } from '../../api/Backend';
import { ChannelModel } from '../../api/EpgApi.types';
import { ICustomCmsRibbon, IRibbon2Container } from '../../api/models/AndromeTV.types';

/**
 * @property {Backend} backend
 * @property {string} url
 * @property {AdultType} adult
 * @property {number} pageOffset
 * @property {FocusType} focusType
 * @property {ChannelModel[]} channels
 */
export class RibbonRequest {
  private extraData: ExtraDataType;
  private backend: Backend;
  private url: string;
  private adult: AdultType;
  private pageOffset: number;
  private focusType: FocusType;
  private cachePrefix: string;
  private channels: ChannelModel[];
  constructor(backend: Backend, ribbon: ICustomCmsRibbon, channels: ChannelModel[]) {
    this.extraData = ExtraDataType.NONE;
    if (ribbon.properties) {
      if (!ribbon.properties.includes('HIDE_MINI_DETAIL')) {
        this.extraData = ExtraDataType.MINI;
      }
    }
    this.backend = backend;
    this.url = ribbon.url;
    this.adult = AdultType.False;
    this.pageOffset = 0;
    this.focusType = FocusType.ANY;
    this.cachePrefix = 'nebula-cache://';
    this.channels = channels;
  }

  /**
   * load ribbon content
   */
  load(): Promise<IRibbon2Container | null> {
    if (!this.url) {
      Log.error('[RibbonRequest] No url specified');
      return Promise.resolve(null);
    }
    const processedUrl = this._replaceParameters(this.url);
    if (this.url.startsWith(this.cachePrefix)) {
      return this._createCachedRibbonContent(processedUrl);
    }
    return CmsApi.ribbonContent(this.backend, processedUrl);
  }

  /**
   * create a ribbon container with cached local content
   */
  _createCachedRibbonContent(processedUrl: string): Promise<IRibbon2Container | null> {
    Log.warn('[RibbonRequest] Nebula cache urldetected', processedUrl);
    const pos = processedUrl.indexOf('?');
    const endIndex = pos !== -1 ? pos : undefined;
    const url = processedUrl.toLowerCase().slice(this.cachePrefix.length, endIndex);
    // console.log('[RibbonRequest] url set to', url);
    switch (url) {
      case 'favorite/channel/logo':
        return new FavoriteRibbonRequest(this.backend, FavoriteRequestType.CHANNEL_LOGO, this.channels).load();
      case 'favorite/channel/toggle':
        return new FavoriteRibbonRequest(this.backend, FavoriteRequestType.CHANNEL_TOGGLE, this.channels).load();
      default:
        Log.error('[RibbonRequest] unsupported cache type', url);
        return Promise.resolve(null);
    }
  }

  /**
   * update the request for the provided cms properties
   */
  updateForProperties(cmsProperties: string[]) {
    if (cmsProperties.includes(CmsProperty.FocusFuture)) {
      this.focusType = FocusType.FUTURE;
    } else if (cmsProperties.includes(CmsProperty.FocusPast)) {
      this.focusType = FocusType.PAST;
    } else {
      this.focusType = FocusType.ANY;
    }
  }

  /**
   * Replaces ribbon url parameters with the correct values
   */
  _replaceParameters(url: string): string {
    let result = url;
    const now = new Date();

    if (result.includes('${now:M1}')) {
      result = result.replace(/\$\{now:M1}/g, this._truncatedUTCString(now, 1));
    }
    if (result.includes('${now:M5}')) {
      result = result.replace(/\$\{now:M5}/g, this._truncatedUTCString(now, 5));
    }
    if (result.includes('${now:M10}')) {
      result = result.replace(/\$\{now:M10}/g, this._truncatedUTCString(now, 10));
    }
    if (result.includes('${adult')) {
      result = result.replace(/\$\{adult}/g, this.adult || AdultType.False);
    }
    if (result.includes('${page}')) {
      result = result.replace(/\$\{page}/g, `${this.pageOffset}`);
    }
    if (result.includes('${entitlementId}')) {
      const val = this.backend.sessionInfo?.device.entitlementId || '';
      result = result.replace(/\$\{entitlementId}/g, val);
    }
    if (result.includes('${catalogId')) {
      const val = this.backend.deviceProfile?.profile.catalog || '';
      result = result.replace(/\$\{catalogId}/g, val);
    }
    if (result.includes('${profileId')) {
      const val = this.backend.deviceProfile?.id || '';
      result = result.replace(/\$\{profileId}/g, val);
    }
    if (result.includes('${loggedIn}')) {
      result = result.replace(/\$\{loggedIn}/g, this.backend.bearer ? 'true' : 'false');
    }
    if (result.includes('${deviceType}')) {
      result = result.replace(/\$\{deviceType}/g, Environment.system);
    }

    if (result.includes('${platformType}')) {
      result = result.replace(/\$\{platformType}/g, Environment.system);
    }

    if (result.includes('${extraData}')) {
      result = result.replace(/\$\{extraData}/g, this.extraData);
    }

    if (result.includes('${actionFilter}')) {
      result = result.replace(/\$\{actionFilter}/g, ottActionFilter);
    }

    if (result.includes('${focusType}')) {
      result = result.replace(/\$\{focusType}/g, this.focusType);
    }

    return result;
  }

  /**
   * Clamp the minutes of a date to the specified value and return a formatted string
   * @param {Date} date - the date that will be formatted
   * @param {number} minutes - the value used for clamping issues
   * @returns {string} - a formatted string of the date
   * @private
   */
  _truncatedUTCString(date: Date, minutes: number): string {
    const res = new Date(date);
    const minute = res.getMinutes() % minutes;

    res.setMinutes(res.getMinutes() - minute);
    return formatDate(res);
  }

  set pageIdx(idx: number) {
    this.pageOffset = idx;
  }
}
